import { useTranslation } from "@coworker/locales";
import { useCallback, useMemo, useState } from "react";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { ItemNoAndTypeWithQuantity } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { MAX_QUANTITY_NUMBER } from "@coworker/app/src/components/InputPopup/AddMultipleArticles.helper";
import { FixaToast } from "@coworker/apprestructured/src/shared/wrappers/FixaToast/FixaToast";
import { LoadingArticle } from "./LoadingArticle/LoadingArticle";
import { SelectedArticles } from "./SelectedArticles/SelectedArticles";
import { SelectedArticlesHeader } from "./SelectedArticles/SelectedArticlesHeader";
import ListItem from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ListItem";
import ErrorAddOn from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";
import ImagePreviewAddOn from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ImagePreviewAddOn/ImagePreviewAddOn";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import ModalColumn from "@coworker/apprestructured/src/task-management/components/ModalColumn/ModalColumn";
import StyledModalFooter from "../StyledModalFooter/StyledModalFooter";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import { DefaultScannerComponent } from "@ingka-group-digital/flexa-scanner";
import { ScanAndSearchSlot } from "@coworker/apprestructured/src/scan-and-search/ScanAndSearchSlot";
import { useGetItemOrLocalCombination } from "@coworker/apprestructured/src/shared/hooks/item/useGetItemOrLocalCombination";

/** Item enriched with a user-specified quantity for use inside AttachArticles. */
export type ItemWithQuantity = Item & { quantity: number };

type AttachArticlesProps = {
  items: ItemWithQuantity[];
  setItemNosAndType: (newItemNosAndType: ItemNoAndTypeWithQuantity[]) => void;
  disabled: boolean;
  maxArticles: number;
  showQuantity?: boolean;
  showError?: boolean;
  minItems?: number;
};

export const AttachArticles = ({
  items,
  setItemNosAndType,
  disabled,
  maxArticles,
  showQuantity,
  showError = false,
  minItems = 0,
}: AttachArticlesProps) => {
  const [showModal, setShowModal] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [modalItems, setModalItems] = useState<ItemWithQuantity[]>(items);

  const {
    saveGuardedFunction: handleCloseClick,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowModal(false);
      setShowToast(false);
      setToastMessage("");
    }, [])
  );

  const handleListViewItemClick = useCallback(() => {
    setShowModal(true);
    setModalItems(items);
  }, [items]);

  const { t } = useTranslation();

  const handleDeleteArticle = useCallback(
    (no: string) => {
      setModalItems((prev) => prev.filter((modalItem) => modalItem.no !== no));
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleQuantityChange = useCallback(
    (no: string, quantity: number) => {
      const clamped = Math.min(Math.max(1, quantity), MAX_QUANTITY_NUMBER);
      setModalItems((prev) =>
        prev.map((item) =>
          item.no === no ? { ...item, quantity: clamped } : item
        )
      );
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleToggleItemSelect = useCallback(
    (item: Item) => {
      const alreadySelected = modalItems.some(
        (modalItem) => modalItem.no === item.no
      );

      if (!alreadySelected && modalItems.length >= maxArticles) {
        setToastMessage("tooManyArticlesAddedString");
        setShowToast(true);
        return;
      }

      setModalItems((prev) =>
        alreadySelected
          ? prev.filter((modalItem) => modalItem.no !== item.no)
          : [...prev, { ...item, quantity: 1 }]
      );
      enableSaveGuard();
    },
    [modalItems, maxArticles, enableSaveGuard]
  );

  const getItemOrLocalCombination = useGetItemOrLocalCombination();

  const handleScannedArticle = useCallback(
    async (itemNo?: string) => {
      setIsCameraOpen(false);
      setShowToast(false);

      if (!itemNo || isLoading) return;

      if (modalItems.length >= maxArticles) {
        setToastMessage("tooManyArticlesAddedString");
        setShowToast(true);
        return;
      }

      if (modalItems.some((modalItem) => modalItem.no === itemNo)) {
        setToastMessage("articleAlreadyExistsString");
        setShowToast(true);
        return;
      }

      setIsLoading(true);
      try {
        const item = await getItemOrLocalCombination(itemNo);

        if (item) {
          setModalItems((prev) => [...prev, { ...item, quantity: 1 }]);
          enableSaveGuard();
        } else {
          setToastMessage("couldNotAddArticleString");
          setShowToast(true);
        }
      } catch {
        setToastMessage("couldNotAddArticleString");
        setShowToast(true);
      } finally {
        setIsLoading(false);
      }
    },
    [
      isLoading,
      modalItems,
      maxArticles,
      enableSaveGuard,
      getItemOrLocalCombination,
    ]
  );

  const imageSrcs = useMemo(() => {
    return items.map((item) => item.smallImageUrl ?? item.imageUrl ?? "");
  }, [items]);

  const content = useMemo(() => {
    const searchSlot = (
      <ScanAndSearchSlot
        onResult={({ articleNumber }) => handleScannedArticle(articleNumber)}
        onCameraOpen={() => setIsCameraOpen(true)}
        multiSelect
        selectedItemNos={modalItems.map((item) => item.no)}
        onToggleItemSelect={handleToggleItemSelect}
      />
    );

    if (isCameraOpen) {
      return (
        <DefaultScannerComponent
          onResult={({ articleNumber }) => handleScannedArticle(articleNumber)}
          isCameraOpen={isCameraOpen}
          onCameraOpenChange={setIsCameraOpen}
          searchSlot={searchSlot}
        />
      );
    }

    return (
      <FixaModalBody>
        <ModalColumn gap="medium">
          <DefaultScannerComponent
            onResult={({ articleNumber }) =>
              handleScannedArticle(articleNumber)
            }
            isCameraOpen={isCameraOpen}
            onCameraOpenChange={setIsCameraOpen}
            searchSlot={searchSlot}
          />

          <div>
            <SelectedArticlesHeader numberOfArticles={modalItems.length} />
            <SelectedArticles
              items={modalItems}
              onDeleteArticle={handleDeleteArticle}
              onQuantityChange={handleQuantityChange}
              showQuantity={showQuantity}
            />
            {isLoading && <LoadingArticle />}
          </div>
        </ModalColumn>
      </FixaModalBody>
    );
  }, [
    handleScannedArticle,
    isLoading,
    modalItems,
    handleDeleteArticle,
    handleQuantityChange,
    showQuantity,
    isCameraOpen,
    setIsCameraOpen,
    handleToggleItemSelect,
  ]);

  const handleConfirmClick = useCallback(() => {
    setItemNosAndType(
      modalItems.map(({ no, type, quantity }) => ({ no, type, quantity }))
    );
    setShowModal(false);
    disableSaveGuard();
  }, [setItemNosAndType, modalItems, disableSaveGuard]);

  const errorAddOn = showError ? (
    <ErrorAddOn>
      {t("pleaseAddAtLeastArticlesString", { count: minItems })}
    </ErrorAddOn>
  ) : null;

  const imageAddOn =
    imageSrcs.length > 0 ? <ImagePreviewAddOn srcs={imageSrcs} /> : null;

  return (
    <>
      <ListItem
        title={t(
          `${items.length > 0 ? "articlesString" : "addArticlesString"}`
        )}
        description={
          items.length > 0
            ? items.length === 1
              ? `1 ${t("articleString")}`
              : `${items.length} ${t("articlesString")}`
            : ""
        }
        disabled={disabled}
        onClick={handleListViewItemClick}
        addOn={
          <>
            {imageAddOn}
            {errorAddOn}
          </>
        }
        emphasised={items.length > 0}
      />
      <FixaModal visible={showModal} handleCloseBtn={handleCloseClick}>
        <FixaSheets
          labelledById="task-management-attach-articles-header"
          header={
            <FixaModalHeader
              id="task-management-attach-articles-header"
              title={t("addArticlesString")}
              closeBtnClick={handleCloseClick}
            />
          }
          footer={
            isCameraOpen ? null : (
              <StyledModalFooter>
                <FixaButton
                  text={t("confirmString")}
                  type="primary"
                  onClick={handleConfirmClick}
                />
              </StyledModalFooter>
            )
          }
          fullSize
        >
          {content}
        </FixaSheets>
      </FixaModal>
      <FixaToast
        isOpen={showToast}
        text={toastMessage ? t(toastMessage) : ""}
        onCloseRequest={() => setShowToast(false)}
      />
    </>
  );
};
